import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

type Item = {
  id: string;
  t: string;
  s: string;
  section: string;
  onAct: () => void;
};

export default function CommandPalette({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const nav = useNavigate();

  useEffect(() => { inputRef.current?.focus(); }, []);

  const items = useMemo<Item[]>(() => {
    const go = (to: string) => () => { nav(to); onClose(); };
    return [
      { id: "nav-home",  section: "Go to", t: "Home",         s: "G H",  onAct: go("/") },
      { id: "nav-repo",  section: "Go to", t: "Repository",   s: "G R",  onAct: go("/repository") },
      { id: "nav-dash",  section: "Go to", t: "Dashboards",   s: "G D",  onAct: go("/dashboards") },
      { id: "nav-ask",   section: "Go to", t: "Ask Pulse",    s: "G A",  onAct: go("/ask") },
      { id: "nav-inv",   section: "Go to", t: "Investigate",  s: "G I",  onAct: go("/investigate") },
      { id: "nav-sig",   section: "Go to", t: "Signal Check", s: "G S",  onAct: go("/signal") },
      { id: "nav-qts",   section: "Go to", t: "Quote Explorer", s: "G Q", onAct: go("/quotes") },
      { id: "nav-set",   section: "Go to", t: "Settings",     s: "G ,",  onAct: go("/settings") },

      { id: "subj-mg",  section: "Subjects", t: "Switch to Mixer Grinder",        s: "Active",      onAct: go("/repository") },
      { id: "subj-ox",  section: "Subjects", t: "Switch to OX Category",          s: "Placeholder", onAct: go("/repository") },
      { id: "subj-nx",  section: "Subjects", t: "Switch to Tata Nexon",           s: "Placeholder", onAct: go("/repository") },

      { id: "ask-nps",  section: "Saved questions", t: "Compare NPS · Preethi vs Bajaj · Tier 2",   s: "Ask", onAct: go("/ask") },
      { id: "ask-pain", section: "Saved questions", t: "Which durability concerns dominate Kolkata qual?", s: "Ask", onAct: go("/ask") },
      { id: "inv-drop", section: "Past investigations", t: "Why did Preethi NPS drop 6 points in Q2?", s: "Investigate", onAct: go("/investigate") },
      { id: "sig-kol",  section: "Signal alerts",       t: "Amazon Kolkata · flagged manipulation",    s: "Signal",      onAct: go("/signal") },
    ];
  }, [nav, onClose]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const l = q.toLowerCase();
    return items.filter((i) => (i.t + " " + i.section + " " + i.s).toLowerCase().includes(l));
  }, [q, items]);

  useEffect(() => { setCursor(0); }, [q]);

  // keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(filtered.length - 1, c + 1)); }
      if (e.key === "ArrowUp")   { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
      if (e.key === "Enter")     { e.preventDefault(); filtered[cursor]?.onAct(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filtered, cursor]);

  // group by section preserving order
  const grouped: Record<string, Item[]> = {};
  filtered.forEach((i) => { (grouped[i.section] ||= []).push(i); });

  // cursor mapped to flat filtered list — compute absolute index per item
  const indexMap = new Map<string, number>();
  filtered.forEach((i, idx) => indexMap.set(i.id, idx));

  return (
    <div className="cmdk-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cmdk-panel" onMouseDown={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="cmdk-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Jump to a subject, a saved question, or a past investigation…"
        />
        <div className="cmdk-list">
          {Object.keys(grouped).length === 0 && (
            <div style={{ padding: "20px 12px", color: "var(--ink-60)", fontFamily: "var(--display)", fontStyle: "italic" }}>
              No matches. Try a subject name, a brand, or a past question.
            </div>
          )}
          {Object.entries(grouped).map(([sec, list]) => (
            <div key={sec}>
              <div className="cmdk-sec">{sec}</div>
              {list.map((i) => {
                const abs = indexMap.get(i.id)!;
                const active = abs === cursor;
                return (
                  <div
                    key={i.id}
                    className={"cmdk-item" + (active ? " active" : "")}
                    onMouseEnter={() => setCursor(abs)}
                    onClick={i.onAct}
                  >
                    <IconFor section={sec} />
                    <span className="t">{i.t}</span>
                    <span className="s">{i.s}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="cmdk-foot">
          <span><kbd>↵</kbd> Open &nbsp;&nbsp; <kbd>↑↓</kbd> Navigate &nbsp;&nbsp; <kbd>esc</kbd> Close</span>
          <span>Market Pulse</span>
        </div>
      </div>
    </div>
  );
}

function IconFor({ section }: { section: string }) {
  if (section === "Go to") return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>;
  if (section === "Subjects") return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 22 8 12 14 2 8 12 2"/><polyline points="2 14 12 20 22 14"/></svg>;
  if (section === "Saved questions") return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h16v11H8l-4 4V5z"/></svg>;
  if (section === "Past investigations") return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><line x1="16" y1="16" x2="21" y2="21"/></svg>;
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 L22 20 L2 20 Z"/></svg>;
}
